/**
 * AbGroupPicker - пикер эксперимента/групп/контроля для «Сравнения групп»
 * ==========================================================================
 *
 * Popover: список экспериментов окна регистраций → чекбоксы групп внутри
 * выбранного эксперимента (2-3 группы) → выбор контрольной группы → горизонт
 * ретеншена (1/3/7/14/30 дней). По образцу `RetentionGeoFilter.js`.
 *
 * Список экспериментов грузится лениво при каждом открытии поповера (тот же
 * резон, что и у гео-фильтра: окно дат/проект могли смениться, а повторный
 * запрос почти всегда бьёт в клиентский TTL-кэш).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { FlaskConical } from 'lucide-react';
import { useFilters } from '../../../hooks/useFilters';
import { FiltersService } from '../../../services/filtersService';
import { AbtestService } from '../../../services/abtestService';
import { abtest as abtestSpecs } from '@ggstat/chart-specs';

const HORIZON_OPTIONS = abtestSpecs.picker.horizonOptions;

const fmtInt = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });

/** Контроль по умолчанию: помеченная сервером группа, иначе крупнейшая по устройствам. */
function pickDefaultControl(groups) {
  const byName = (groups || []).find((g) => g.isControlByName);
  if (byName) return byName.group;
  const sorted = [...(groups || [])].sort((a, b) => b.devices - a.devices);
  return sorted[0]?.group || '';
}

export function AbGroupPicker({
  selection,
  onSelectExperiment,
  onToggleGroup,
  onSetControl,
  onSetHorizonDays
}) {
  const { filters } = useFilters();
  const [open, setOpen] = useState(false);
  const [experiments, setExperiments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadExperiments = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const { startDate, endDate } = FiltersService.toQueryParams(filters);
      const rows = await AbtestService.getExperiments({ from: startDate, to: endDate });
      setExperiments(rows);
    } catch (e) {
      setError(e?.message || 'Не удалось загрузить список экспериментов');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) loadExperiments();
  };

  const currentExperiment = useMemo(
    () => (experiments || []).find((exp) => exp.experiment === selection.experiment) || null,
    [experiments, selection.experiment]
  );

  const handlePickExperiment = (exp) => {
    const groups = (exp.groups || []).map((g) => g.group);
    onSelectExperiment(exp.experiment, { groups, control: pickDefaultControl(exp.groups) });
  };

  // Автовыбор первого (крупнейшего — сервер сортирует по totalDevices) эксперимента,
  // если пользователь ещё ничего не выбирал.
  useEffect(() => {
    if (!selection.experiment && experiments && experiments.length > 0) {
      handlePickExperiment(experiments[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experiments]);

  return (
    <div className="flex items-center gap-2">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1 text-xs">
            <FlaskConical className="h-3 w-3" />
            {selection.experiment || 'Эксперимент'}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-3 space-y-3">
          {loading && <p className="text-xs text-muted-foreground">Загрузка экспериментов…</p>}
          {error && <p className="text-xs text-destructive">{error}</p>}
          {!loading && !error && (experiments || []).length === 0 && (
            <p className="text-xs text-muted-foreground">Нет экспериментов за выбранный период</p>
          )}

          {!loading && !error && experiments && experiments.length > 0 && (
            <div className="space-y-0.5 max-h-40 overflow-y-auto">
              {experiments.map((exp) => (
                <button
                  key={exp.experiment}
                  type="button"
                  onClick={() => handlePickExperiment(exp)}
                  className={`w-full text-left px-2 py-1 rounded text-xs hover:bg-muted/50 ${
                    selection.experiment === exp.experiment ? 'bg-muted font-medium' : ''
                  }`}
                >
                  {exp.experiment} <span className="text-muted-foreground">({fmtInt(exp.totalDevices)})</span>
                </button>
              ))}
            </div>
          )}

          {currentExperiment && (
            <>
              <div className="space-y-1 border-t pt-2">
                <p className="text-xs font-medium">Группы</p>
                {currentExperiment.groups.map((g) => (
                  <label
                    key={g.group}
                    className="flex items-center gap-2 px-1 py-1 rounded hover:bg-muted/50 cursor-pointer text-xs"
                  >
                    <Checkbox
                      checked={selection.groups.includes(g.group)}
                      onCheckedChange={() => onToggleGroup(g.group)}
                    />
                    <span>{g.group}</span>
                    <span className="ml-auto text-muted-foreground">{fmtInt(g.devices)}</span>
                  </label>
                ))}
              </div>

              <div className="space-y-1 border-t pt-2">
                <p className="text-xs font-medium">Контрольная группа</p>
                <Select value={selection.control} onValueChange={onSetControl}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Контроль" />
                  </SelectTrigger>
                  <SelectContent>
                    {selection.groups.map((g) => (
                      <SelectItem key={g} value={g}>
                        {g}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="space-y-1 border-t pt-2">
            <p className="text-xs font-medium">Горизонт ретеншена (дней)</p>
            <div className="flex gap-1">
              {HORIZON_OPTIONS.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => onSetHorizonDays(h)}
                  className={`px-2 py-1 rounded border text-xs ${
                    selection.horizonDays === h ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {h}
                </button>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default AbGroupPicker;
